import { ContentComment } from "@/types/content-comment";

// 최대 중첩 깊이 (0부터 시작, 2레벨 = depth 0~1)
const MAX_NESTING_DEPTH = 1;

/**
 * 평탄한 댓글 배열을 중첩된 트리 구조로 변환 (최대 2레벨 중첩)
 * depth 1 이후의 답글은 부모와 같은 레벨에 평탄하게 추가됨
 * @param comments 데이터베이스에서 가져온 평탄한 댓글 배열
 * @returns 트리 구조로 변환된 최상위 댓글 배열
 */
export function buildContentCommentTree(
    comments: ContentComment[]
): ContentComment[] {
    // 댓글 ID를 키로 하는 맵 생성 (빠른 조회를 위해)
    const commentMap = new Map<string, ContentComment>();
    const rootComments: ContentComment[] = [];

    // 첫 번째 패스: 모든 댓글을 맵에 저장하고 replies 배열 초기화
    comments.forEach((comment) => {
        commentMap.set(comment.id, {
            ...comment,
            replies: [],
            depth: 0, // 초기값, 나중에 계산됨
            isExpanded: false // 기본값: 답글 목록 접힘
        });
    });

    // depth가 MAX_NESTING_DEPTH인 가장 가까운 조상을 찾는 헬퍼 함수
    const findFlattenTarget = (commentId: string): ContentComment | null => {
        const comment = commentMap.get(commentId);
        if (!comment) return null;

        // 부모가 없으면 null
        if (!comment.parent_comment_id) {
            return null;
        }

        const parent = commentMap.get(comment.parent_comment_id);
        if (!parent) return null;

        // 부모가 MAX_NESTING_DEPTH이면 부모에게 평탄화
        if (parent.depth === MAX_NESTING_DEPTH) {
            return parent;
        }

        // 부모가 MAX_NESTING_DEPTH보다 작으면 재귀적으로 계속 찾기
        return findFlattenTarget(comment.parent_comment_id);
    };

    // 두 번째 패스: 부모-자식 관계 설정 (최대 2레벨 중첩)
    comments.forEach((comment) => {
        const commentWithReplies = commentMap.get(comment.id)!;

        if (comment.parent_comment_id) {
            const parent = commentMap.get(comment.parent_comment_id);

            if (parent) {
                // 부모의 depth 확인
                const parentDepth = parent.depth ?? 0;
                if (parentDepth < MAX_NESTING_DEPTH) {
                    // 부모 depth가 MAX_NESTING_DEPTH 미만 → 직접 자식으로 추가
                    parent.replies!.push(commentWithReplies);
                    commentWithReplies.depth = parentDepth + 1;
                } else {
                    // 부모가 이미 MAX_NESTING_DEPTH → 같은 레벨에 평탄화
                    const flattenTarget = findFlattenTarget(
                        comment.parent_comment_id
                    );
                    if (flattenTarget) {
                        flattenTarget.replies!.push(commentWithReplies);
                        commentWithReplies.depth = MAX_NESTING_DEPTH;
                    } else {
                        // 평탄화 대상을 찾을 수 없으면 부모에 추가
                        parent.replies!.push(commentWithReplies);
                        commentWithReplies.depth = MAX_NESTING_DEPTH;
                    }
                }
            } else {
                // 부모를 찾을 수 없으면 최상위로 처리 (데이터 무결성 보호)
                rootComments.push(commentWithReplies);
                commentWithReplies.depth = 0;
            }
        } else {
            // 부모가 없는 최상위 댓글
            rootComments.push(commentWithReplies);
            commentWithReplies.depth = 0;
        }
    });

    // 각 레벨에서 created_at 기준으로 정렬 (최신순)
    const sortByCreatedAt = (commentList: ContentComment[]) => {
        commentList.sort(
            (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
        );
        commentList.forEach((comment) => {
            if (comment.replies && comment.replies.length > 0) {
                sortByCreatedAt(comment.replies);
            }
        });
    };

    sortByCreatedAt(rootComments);

    return rootComments;
}
